<template>
  <div>
    <v-card-subtitle
      class="text-sm pa-0 pt-3 mb-2"
    >
      顧客タグ
    </v-card-subtitle>
    <custom-combobox
      v-model="customerTableTaggingsAttributes"
      :items="customerTags"
      :filter="filtering"
      multiple
      chips
      solo
      hide-details
      hide-selected
      dense
      return-object
      :menu-props="{ auto: true, offsetY: true }"
      :prepend-inner-icon="icons.mdiPound"
      :loading="isLoading"
      no-data-text="選択可能なデータがありません"
      :item-value="getCustomerTagId"
      @change="select"
    >
      <template #selection="{ attrs, item }">
        <v-chip
          v-bind="attrs"
          small
          close
          :color="isToDestroy(item.customerTagId) ? 'error' : ''"
          :class="isToDestroy(item.customerTagId) ? 'v-chip-light-bg error--text' : ''"
          @click:close="remove(item)"
        >
          {{ typeof item !== 'string' ? item.attributes.name : item }}
        </v-chip>
      </template>

      <template #item="{ item }">
        <v-list-item-action>
          <v-checkbox
            hide-details
            disabled
            :ripple="false"
          />
        </v-list-item-action>

        <v-list-item-content>
          <v-list-item-title>
            {{ item.attributes.name }}
          </v-list-item-title>
        </v-list-item-content>
      </template>
    </custom-combobox>
  </div>
</template>

<script>
import { ref, computed, inject } from '@vue/composition-api'
import { mdiPound } from '@mdi/js'
import {
  filter,
  each,
  findIndex,
  find,
  includes,
  map,
} from 'lodash'
import TableApi from '@/api/waiter/Table'
import CustomCombobox from '@/views/components/custom_vuetify/CustomCombobox'

export default {
  components: {
    CustomCombobox,
  },
  props: {
    tableId: {
      type: [Number, String],
      default: null,
      validator: value => !Number.isNaN(Number(value)),
    },
    customerTableTaggings: {
      type: Array,
      default: () => [],
    },
    updateEveryTime: {
      type: Boolean,
      default: true,
    },
  },
  setup(props, { emit }) {
    const isLoading = ref(false)
    const customerTableTaggingsAttributes = ref([])

    const customerTags = inject('customerTags', [])

    const customerTagIdsToDestroy = computed(() => {
      const destroyTargets = filter(customerTableTaggingsAttributes.value, '_destroy')

      return map(destroyTargets, o => Number(o.customerTagId))
    })

    const isToDestroy = customerTagId => {
      return includes(customerTagIdsToDestroy.value, Number(customerTagId))
    }

    const buildCustomerTableTaggingAttributes = ({
      id = null,
      customerTagId,
      tableId = props.tableId || null,
      _destroy = false,
      options = {},
    }) => {
      return {
        id,
        customerTagId,
        tableId,
        _destroy,
        ...options,
      }
    }

    const getCustomerTagId = item => {
      let id
      switch (item.type) {
        case 'customerTag':
          // eslint-disable-next-line prefer-destructuring
          id = item.id
          break

        default:
          id = item.customerTagId
          break
      }

      return Number(id)
    }

    const update = async attributes => {
      const res = await TableApi.updateTable({
        tableId: props.tableId,
        params: {
          customerTableTaggingsAttributes: attributes,
        },
      })

      if (res?.data?.data) return res.data.data

      return res
    }

    const filtering = (item, queryText, itemText) => {
      if (!queryText) return false

      return item?.attributes?.name?.toString().toLowerCase().indexOf(queryText.toString().toLowerCase()) > -1
        || item?.attributes?.shortcut1?.toString().toLowerCase().indexOf(queryText.toString().toLowerCase()) > -1
        || item?.attributes?.shortcut2?.toString().toLowerCase().indexOf(queryText.toString().toLowerCase()) > -1
    }

    const select = async items => {
      const newCustomerTags = filter(items, obj => obj.type === 'customerTag')
      const addTargets = []

      // eslint-disable-next-line no-restricted-syntax
      for (const customerTag of newCustomerTags) {
        const addTarget = buildCustomerTableTaggingAttributes({
          customerTagId: customerTag.id,
          options: { attributes: customerTag.attributes },
        })

        if (addTarget.tableId && props.updateEveryTime) {
          // eslint-disable-next-line no-await-in-loop
          const data = await update([addTarget])

          const id = find((data?.attributes?.customerTableTaggings?.data || []), o => Number(o.attributes.customerTagId) === Number(customerTag.id))?.id
          if (id) addTarget.id = id
          // eslint-disable-next-line no-continue
          else continue

          if (data) emit('updated:table', data)
        }
        addTargets.push(addTarget)
      }

      // コンボボックスが一時的に追加した生の customerTag 型アイテムを
      // 正規化された customerTableTagging 形式に置き換えてから全件 emit する
      // （削除マーク済みの _destroy アイテムを含む全状態を維持するため）
      const existingTaggings = filter(customerTableTaggingsAttributes.value, obj => obj.type !== 'customerTag')
      customerTableTaggingsAttributes.value = [...existingTaggings, ...addTargets]
      emit('updated', customerTableTaggingsAttributes.value)
    }

    const remove = async item => {
      const removeTargetIdx = findIndex(
        customerTableTaggingsAttributes.value,
        obj => Number(obj.customerTagId) === Number(item.customerTagId),
      )
      const removeTarget = customerTableTaggingsAttributes.value[removeTargetIdx]
      if (removeTarget?.id) {
        // eslint-disable-next-line no-underscore-dangle
        removeTarget._destroy = !removeTarget._destroy
      } else {
        customerTableTaggingsAttributes.value.splice(removeTargetIdx, 1)
      }

      if (removeTarget?.id && props.updateEveryTime) {
        const data = await update([removeTarget])
        if (data) emit('updated:table', data)
        customerTableTaggingsAttributes.value.splice(removeTargetIdx, 1)
      }

      emit('updated', customerTableTaggingsAttributes.value)
    }

    const init = customerTableTaggings => {
      customerTableTaggingsAttributes.value = []

      if (customerTableTaggings.length > 0) {
        each(customerTableTaggings, obj => {
          const { customerTagId, tableId, customerTag } = obj.attributes

          customerTableTaggingsAttributes.value.push(buildCustomerTableTaggingAttributes({
            id: obj.id,
            customerTagId,
            tableId,
            options: {
              attributes: {
                ...customerTag.data.attributes,
              },
            },
          }))
        })
      }
    }

    init(props.customerTableTaggings)
    emit('init:init-func', init)

    return {
      // data
      isLoading,
      customerTags,
      customerTableTaggingsAttributes,

      // computed
      customerTagIdsToDestroy,

      // methods
      getCustomerTagId,
      select,
      remove,
      isToDestroy,
      filtering,

      icons: {
        mdiPound,
      },
    }
  },
}
</script>
